import { PROMO_CODE_STATUS_LABELS, derivePromoCodeStatus } from './promoCodes';

// Normalizes the campaign-service eligibility response into the shape the
// Campaigns tab renders. The pro-facing `/v1/eligibilities` returns snake_case
// while GOTU-7046 declares camelCase JSON tags for the admin route, so every
// field accepts both spellings rather than betting on one — the same hedge
// `domain/Promotions/mappers.js` already makes.

// Shown wherever a value is absent. A blank cell reads as a rendering bug to
// CS; an em dash reads as "the service didn't send one".
export const EMPTY_VALUE = '—';

// `first_shift_completed` and `shift_completed` are the two trigger values
// mobile's CampaignKey ships, and the design shows the same copy on both.
const TRIGGER_TYPE_LABELS = {
  first_shift_completed: 'Complete 1 Shift',
  shift_completed: 'Complete 1 Shift',
};

// `paused` is not in the design (which only defines Live/Closed), but folding
// it into "Closed" would tell CS a running campaign had ended — the one
// question this tab exists to answer. It gets its own label. See GOTU-7054
// open question #1 if design later rules otherwise.
const CAMPAIGN_STATUS_LABELS = {
  active: 'Live',
  paused: 'Paused',
  ended: 'Closed',
};

// The endpoint only derives `eligible` / `completed` today. The story's other
// four pro states (Application Pending, Shift Confirmed, Cancelled, Locked —
// Campaign Ended) are not on the wire and need a campaign-service change
// first; the fallback below renders them readably if they ever arrive.
const PRO_STATUS_LABELS = {
  eligible: 'Eligible',
  completed: 'Completed',
};

// "first_shift_completed" -> "First Shift Completed". The fallback for enum
// values we have not seen: a campaign added server-side stays readable here
// without an Admin Panel release, which is the point of AC 4.
const titleCaseToken = (value) =>
  String(value)
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const labelFor = (labels, value) => {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;

  return labels[value] ?? titleCaseToken(value);
};

// "Tue, Aug 18, 2026", per the design. Deliberately not ProPrograms'
// formatPromotionDate, which renders "Tue, August 18th, 2026" — long month
// plus ordinal is a different format. Renders in the admin's local timezone,
// the same as every other date on the profile.
export const formatCampaignDate = (value) => {
  if (!value) return EMPTY_VALUE;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// One eligibility row. `amount` is on the wire but has no column in the
// design, so it is deliberately not carried through.
export const mapProfessionalCampaign = (eligibility = {}) => {
  const triggerType = eligibility.trigger_type ?? eligibility.triggerType ?? null;
  const matchedAt = eligibility.matched_at ?? eligibility.matchedAt ?? null;
  const status = eligibility.status ?? null;
  const proStatus = eligibility.pro_status ?? eligibility.proStatus ?? null;

  return {
    campaignId: eligibility.campaign_id ?? eligibility.campaignId ?? null,
    campaignName: eligibility.campaign_name ?? eligibility.campaignName ?? '',
    triggerType,
    triggerTypeLabel: labelFor(TRIGGER_TYPE_LABELS, triggerType),
    matchedAt,
    matchedAtLabel: formatCampaignDate(matchedAt),
    status,
    statusLabel: labelFor(CAMPAIGN_STATUS_LABELS, status),
    proStatus,
    proStatusLabel: labelFor(PRO_STATUS_LABELS, proStatus),
  };
};

// ---------------------------------------------------------------------------
// Promo codes (GOTU-5661 list + detail, GOTU-6663 office view)
// ---------------------------------------------------------------------------

/**
 * An instant as `MM/DD/YY` in the admin's timezone — the format every date on
 * the Promo Codes mockups uses. `startsAt`/`endsAt` are timestamps, not
 * calendar dates, so building a Date is right here where it would be wrong
 * for awards-service's `YYYY-MM-DD` fields.
 */
export const formatPromoDate = (value) => {
  if (!value) return EMPTY_VALUE;

  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return EMPTY_VALUE;

  const month = String(instant.getMonth() + 1).padStart(2, '0');
  const day = String(instant.getDate()).padStart(2, '0');

  return `${month}/${day}/${String(instant.getFullYear()).slice(-2)}`;
};

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/**
 * The discount as the mockups show it: "50%" for a percent code.
 *
 * Fixed-amount codes became applyable in campaign-service #74 and render as
 * dollars ("$25.00") — the service stores `value` in cents for that kind, and
 * rendering it as "0%" on the surface an admin opens to check a code would be
 * exactly wrong. A spec that does not decode is an em dash, matching the
 * service's own choice of `discount: null` over a fabricated zero.
 *
 * Accepts either the campaign's `mechanismSpec` ({ discountKind, value,
 * maxCents }) or the office history's `discount` ({ kind, value, maxAmount }):
 * one column, two spellings on the wire.
 */
export const formatDiscount = (spec) => {
  if (!spec) return EMPTY_VALUE;

  const kind = spec.discountKind ?? spec.kind;
  const value = Number(spec.value);
  if (!Number.isFinite(value)) return EMPTY_VALUE;

  if (kind === 'percent') return `${value}%`;
  if (kind === 'fixed') return usd.format(value / 100);

  return EMPTY_VALUE;
};

/**
 * A quota cap as the ticket wants it: the number, or "0" when uncapped.
 *
 * The wire distinguishes "no cap" (null/absent) from "a cap of zero", but the
 * ticket collapses both to 0 for the readout. `EMPTY_VALUE` is reserved for a
 * cap we could not read at all.
 */
export const formatMaxUses = (cap) => {
  if (cap === null || cap === undefined) return '0';

  const count = Number(cap);

  return Number.isFinite(count) ? String(count) : EMPTY_VALUE;
};

export const AUTO_APPLY_LABELS = { true: 'Yes', false: 'No' };

// The targeting keys campaign-service's catalog defines for an office-identity
// campaign, mapped onto the three lists the detail page reads out. Anything
// else in a rule — postal codes, thresholds — has no column on the design and
// is not carried through.
const TARGETING_LISTS = {
  shift_profession: 'professions',
  office_address_state: 'states',
  office_dso_code: 'dsoCodes',
};

const leafValues = (value) => {
  if (Array.isArray(value)) return value.map(String);
  if (value === null || value === undefined) return [];

  return [String(value)];
};

/**
 * Flattens a targeting-rule tree into the three "included" lists the ticket
 * asks for: profession types, states, DSOs — each empty when the rule does not
 * constrain it, which the page renders as "All".
 *
 * The tree is `{and:[…]}` / `{or:[…]}` branches over `{key, operator, value}`
 * leaves (or `{matchAll: true}`). This readout deliberately ignores the
 * boolean structure: it answers "which values does this rule mention" rather
 * than "exactly who matches", which is what a look-only V2 needs and all the
 * design has room for. Only `eq` and `in` leaves contribute — a negation or
 * range operator on these keys would need a richer readout than a list.
 *
 * Values are de-duplicated and sorted so the readout is stable across saves.
 */
export const flattenTargetingRule = (rule) => {
  const lists = { professions: [], states: [], dsoCodes: [] };

  const walk = (node) => {
    if (!node || typeof node !== 'object') return;

    if (Array.isArray(node.and)) node.and.forEach(walk);
    if (Array.isArray(node.or)) node.or.forEach(walk);

    const list = TARGETING_LISTS[node.key];
    if (list && (node.operator === 'eq' || node.operator === 'in')) {
      lists[list].push(...leafValues(node.value));
    }
  };

  walk(rule);

  return Object.fromEntries(
    Object.entries(lists).map(([name, values]) => [name, [...new Set(values)].sort()]),
  );
};

/**
 * One campaign from `GET /v1/admin/campaigns` (or `/:id`) as a promo-code row.
 *
 * `status` is the ticket's date-derived status (see derivePromoCodeStatus),
 * not the lifecycle value, which is kept alongside as `campaignStatus`.
 *
 * `maxUses` is the per-office cap (`quotaCaps.perIdentity`). That is the cap
 * nearly every code in the data actually sets, it is the figure the office
 * tab calls "Max Uses" too, and it is what an admin answering "how many times
 * can this office use it" needs; rendering the campaign-wide cap there read
 * as "unlimited" for codes that were capped. The campaign-wide cap
 * (`quotaCaps.total`) is carried separately for the detail page.
 */
export const mapPromoCode = (campaign = {}, now = Date.now()) => {
  const status = derivePromoCodeStatus(
    {
      campaignStatus: campaign.status,
      startsAt: campaign.startsAt,
      endsAt: campaign.endsAt,
    },
    now,
  );
  const autoApply = Boolean(campaign.autoApply);
  const quotaCaps = campaign.quotaCaps ?? {};

  return {
    id: campaign.id,
    name: campaign.name ?? '',
    code: campaign.promoCode ?? '',
    campaignStatus: campaign.status ?? null,
    status,
    statusLabel: PROMO_CODE_STATUS_LABELS[status] ?? titleCaseToken(status),
    discountLabel: formatDiscount(campaign.mechanismSpec),
    startsAt: campaign.startsAt ?? null,
    startDateLabel: formatPromoDate(campaign.startsAt),
    endsAt: campaign.endsAt ?? null,
    endDateLabel: formatPromoDate(campaign.endsAt),
    maxUses: quotaCaps.perIdentity ?? null,
    maxUsesLabel: formatMaxUses(quotaCaps.perIdentity),
    campaignMaxUses: quotaCaps.total ?? null,
    campaignMaxUsesLabel: formatMaxUses(quotaCaps.total),
    autoApply,
    autoApplyLabel: AUTO_APPLY_LABELS[autoApply],
    targeting: flattenTargetingRule(campaign.targetingRule),
    createdAt: campaign.createdAt ?? null,
  };
};

/**
 * One entry of `GET /v1/admin/offices/:officeId/discounts` as a row on the
 * office's Discounts & Promo Codes tab.
 *
 * `applied` is the ticket's office-level flag: the code reached this office
 * either by being entered (an opt-in exists) or by an automatic campaign
 * granting it directly (a redemption with no opt-in). Every entry the service
 * returns satisfies one of those, so today this is always true — the row for a
 * code the office was merely *eligible* for is the gap noted in the service
 * doc, not something this mapper can invent.
 *
 * `usedCount` excludes freed redemptions: a cancelled or failed one took no
 * quota, which is the same rule the service's own `usesRemaining` applies.
 * `maxUses` is reconstructed from used + remaining because the per-office cap
 * is not on this response; null `usesRemaining` means uncapped, which the
 * ticket renders as 0.
 */
export const mapOfficeDiscount = (entry = {}, now = Date.now()) => {
  const status = derivePromoCodeStatus(
    {
      campaignStatus: entry.campaignStatus,
      startsAt: entry.startsAt,
      endsAt: entry.endsAt,
    },
    now,
  );
  const redemptions = Array.isArray(entry.redemptions) ? entry.redemptions : [];
  const usedCount = redemptions.filter((r) => r.quotaState !== 'freed').length;
  const applied = Boolean(entry.optInId) || redemptions.length > 0;
  const maxUses = entry.usesRemaining === null || entry.usesRemaining === undefined
    ? null
    : usedCount + Number(entry.usesRemaining);

  return {
    campaignId: entry.campaignId,
    campaignName: entry.campaignName ?? '',
    code: entry.code ?? '',
    automatic: Boolean(entry.automatic),
    campaignStatus: entry.campaignStatus ?? null,
    status,
    statusLabel: PROMO_CODE_STATUS_LABELS[status] ?? titleCaseToken(status),
    live: Boolean(entry.live),
    officeEligible: Boolean(entry.officeEligible),
    discountLabel: formatDiscount(entry.discount),
    startDateLabel: formatPromoDate(entry.startsAt),
    endDateLabel: formatPromoDate(entry.endsAt),
    applied,
    appliedLabel: applied ? 'Yes' : 'No',
    appliedByName: entry.appliedByName ?? '',
    usedCount,
    usedCountLabel: String(usedCount),
    maxUses,
    maxUsesLabel: formatMaxUses(maxUses),
  };
};
